import { useCallback, useState } from "react";
import { formatBytes } from "../../lib/formatBytes";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { extensionFromMime, triggerDownload } from "./download";
import { DownloadButton } from "./DownloadButton";
import { BinaryBodyRow } from "./Image";

type Props = {
  url?: string;
  contentType: string;
  fileName?: string;
  sizeBytes?: number;
};

export function Binary({ url, contentType, fileName, sizeBytes }: Props) {
  const [downloading, setDownloading] = useState(false);

  const ext = extensionFromMime(contentType);
  const downloadName = fileName ?? `output.${ext}`;

  const download = useCallback(async () => {
    if (!url || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(url, { credentials: "include" });
      const objectUrl = URL.createObjectURL(await res.blob());
      triggerDownload(objectUrl, downloadName);
      URL.revokeObjectURL(objectUrl);
    } finally {
      setDownloading(false);
    }
  }, [url, downloadName, downloading]);

  return (
    <BinaryBodyRow style={{ paddingLeft: 8 }}>
      <ConnectorLogoContainer
        size={32}
        borderRadius={8}
        hoverBackground={false}
      >
        <Icon name="package-2" size={16} />
      </ConnectorLogoContainer>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {ext.toUpperCase()}
        </Text>
        {sizeBytes != null && sizeBytes > 0 && (
          <Text
            variant="text-small"
            color="var(--text-secondary)"
            style={{ flexShrink: 0 }}
          >
            {formatBytes(sizeBytes)}
          </Text>
        )}
      </div>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <DownloadButton onPress={download} disabled={downloading}>
          <Icon name="arrow-down" size={12} />
          <Text variant="text-small-md">
            {downloading ? "Downloading…" : "Download"}
          </Text>
        </DownloadButton>
      </div>
    </BinaryBodyRow>
  );
}
